import { useDeferredValue, useEffect, useMemo, useState, useTransition } from "react";
import { Plus } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { communitiesQuery } from "@/api/queries/communities";
import { AppHeader } from "@/components/layout";
import { CommunityRow, CommunityRowSkeleton } from "@/components/social";
import { Button, Card, Chip, Dialog, EmptyState, IconButton, SearchInput } from "@/components/ui";
import { formatCompact } from "@/lib/format";
import type { CategorySlug } from "@/api/types";
import { Route } from "@/routes/_app/communities";

export function CommunitiesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/communities" });
  const [query, setQuery] = useState(search.q ?? "");
  const deferredQuery = useDeferredValue(query);
  const [isPending, startTransition] = useTransition();
  const [createOpen, setCreateOpen] = useState(false);
  const selectedCategory: string = search.category ?? "all";
  const communities = useQuery(communitiesQuery({}));

  useEffect(() => {
    startTransition(() => {
      void navigate({
        search: (previous) => ({ ...previous, q: query || undefined }),
        replace: true,
      });
    });
  }, [navigate, query]);

  const categories = useMemo(() => {
    const values = new Set((communities.data ?? []).map((community) => community.category));
    return ["all", ...values];
  }, [communities.data]);

  const filteredCommunities = useMemo(() => {
    const normalizedQuery = deferredQuery.trim().toLowerCase();
    return (communities.data ?? []).filter((community) => {
      const matchesQuery =
        !normalizedQuery ||
        community.name.toLowerCase().includes(normalizedQuery) ||
        community.description.toLowerCase().includes(normalizedQuery);
      const matchesCategory = selectedCategory === "all" || community.category === selectedCategory;
      return matchesQuery && matchesCategory;
    });
  }, [communities.data, deferredQuery, selectedCategory]);

  const clearSearch = () => {
    setQuery("");
  };

  return (
    <>
      <AppHeader />
      <main className="mx-auto min-w-0 max-w-4xl space-y-4 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3">Communities</h1>
        <div className="sm:hidden">
          <IconButton
            label="Create community"
            icon={<Plus size={16} weight="regular" />}
            size="sm"
            onClick={() => setCreateOpen(true)}
          />
        </div>
        <Button
          className="hidden sm:inline-flex"
          size="sm"
          leadingIcon={<Plus size={16} weight="regular" />}
          onClick={() => setCreateOpen(true)}
        >
          Create
        </Button>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          aria-label="Search communities"
          placeholder="Search communities…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="sm:max-w-sm"
        />
        <p className="text-small text-text-muted">{formatCompact(filteredCommunities.length)} communities</p>
      </div>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
        {categories.map((category) => (
          <Chip
            key={category}
            selected={selectedCategory === category}
            onClick={() => {
              void navigate({
                search: (previous) => ({
                  ...previous,
                  category: category === "all" ? undefined : (category as CategorySlug),
                }),
                replace: true,
              });
            }}
          >
            {category === "all" ? "All" : category}
          </Chip>
        ))}
      </div>
      <Card padded={false} className={isPending ? "opacity-60" : undefined}>
        {filteredCommunities.length > 0 ? (
          filteredCommunities.map((community) => (
            <CommunityRow key={community.id} community={community} />
          ))
        ) : (
          <EmptyState
            title="No communities found"
            action={
              <Button variant="secondary" size="sm" onClick={clearSearch}>
                Clear search
              </Button>
            }
          />
        )}
      </Card>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create a community"
        footer={
          <Button size="sm" onClick={() => setCreateOpen(false)}>
            Got it
          </Button>
        }
      >
        <p className="text-body text-text-secondary">
          Community creation opens in a later version of Studside. For now, join an existing community or post in one.
        </p>
      </Dialog>
      </main>
    </>
  );
}

export function CommunitiesPageSkeleton() {
  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-4xl space-y-4 px-4 py-6 sm:px-6">
        <div className="h-8 w-48 animate-shimmer rounded-sm border border-border bg-surface-2" />
        <div className="h-9 w-full max-w-sm animate-shimmer rounded-sm border border-border bg-surface-2" />
        <Card padded={false}>
          {Array.from({ length: 8 }, (_, index) => (
            <CommunityRowSkeleton key={index} />
          ))}
        </Card>
      </main>
    </>
  );
}
